import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useUnit } from 'effector-react';
import { Toaster } from 'sonner';

import { $cartItems, clearCart, removeItemFromCart, updateItemQuantity } from '@/entities/cart/model';

import { getItemBySize } from '@/shared/api/catalog/items';
import { MailIcon } from '@/shared/components/icons/mail';
import { MinusIcon } from '@/shared/components/icons/minus';
import { PlusIcon } from '@/shared/components/icons/plus';
import { TelegramIcon } from '@/shared/components/icons/telegram';
import { WhatsAppIcon } from '@/shared/components/icons/whats-app';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/shared/components/ui/dialog';
import { Title } from '@/shared/components/ui/title';
import { Wrapper } from '@/shared/components/wrapper';
import { copyToClipboard } from '@/shared/lib/copy-to-clipboard';

import { ClearCartDialog } from './clear-cart';

export const Cart = () => {
  const items = useUnit($cartItems);
  const [cartItems, setCartItems] = useState<
    Array<{
      id: string;
      name: string;
      size: string;
      price: number;
      quantity: number;
      imageUrl: string;
      isInStock: boolean;
    }>
  >([]);

  useEffect(() => {
    const fetchCartItems = async () => {
      console.log('Cart items from storage:', items);
      const fetchedItems = await Promise.all(
        items.map(async (item) => {
          try {
            const fetchedItem = await getItemBySize(item.id);
            const sizeData = fetchedItem.attributes.size[0];

            if (sizeData.price === 0) {
              removeItemFromCart(item.id);
              return null;
            }

            return {
              id: item.id,
              name: fetchedItem.attributes.name,
              size: sizeData.size,
              price: sizeData.price,
              quantity: item.quantity,
              imageUrl: fetchedItem.attributes.image.data.attributes.formats.medium.url,
              isInStock: sizeData.isInStock,
            };
          } catch (error) {
            console.error(`Error fetching item with id ${item.id}:`, error);
            return null;
          }
        }),
      );
      setCartItems(fetchedItems.filter((item) => item !== null));
    };

    fetchCartItems();
  }, [items]);

  const total = cartItems.reduce((sum, item) => sum + (item.isInStock ? item.price * item.quantity : 0), 0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const cartSummary = cartItems
    .filter((item) => item.isInStock)
    .map((item) => `${item.name} (${item.size}): ${item.quantity} шт.`)
    .join('\n');

  return (
    <Wrapper className="mt-10 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Title
          type="h1"
          className="text-[26px] font-semibold tracking-tight md:text-[30px] lg:text-[35px] 2xl:text-[70px]"
        >
          Корзина
        </Title>

        {cartItems.length > 0 && <ClearCartDialog />}
      </div>

      {cartItems.length === 0 && (
        <div className="flex flex-col gap-8">
          <p>Сейчас корзина пуста.</p>
          <Button asChild className="mx-auto w-fit px-20">
            <Link href="/catalog">Перейти в каталог</Link>
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-8">
        <ul className="flex flex-col gap-4">
          {cartItems.map((item) => (
            <li key={item.id} className="flex flex-col items-start md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-4">
                <Image
                  src={`${process.env.NEXT_PUBLIC_API_ENDPOINT}${item.imageUrl}`}
                  alt={item.name}
                  width={100}
                  height={75}
                  className="rounded-lg object-cover"
                />
                <div>
                  <Title type="h3">
                    {item.name} ({item.size})
                  </Title>
                  <p className="text-lg text-gray-500">Арт. {item.id}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {item.isInStock ? (
                  <>
                    <p className="text-nowrap text-[20px] tracking-tight lg:text-[30px] 2xl:text-[35px]">
                      {item.price} × {item.quantity} = {item.price * item.quantity} ₽
                    </p>
                    <Button variant="outline" onClick={() => {}} size="icon-lg">
                      <MinusIcon className="h-6 w-6" />
                    </Button>
                    <Button onClick={() => {}} variant="outline" size="icon-lg">
                      <PlusIcon className="h-6 w-6" />
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-red-500">Нет в наличии</p>
                    <Button onClick={() => {}} variant="destructive" size="icon-lg">
                      Удалить
                    </Button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>

        {cartItems.length > 0 && (
          <>
            <div className="h-[1px] w-full bg-primary-foreground" />

            <div className="flex flex-col gap-2">
              <Title type="h3" className="text-right">
                Итого
              </Title>

              <p className="text-right text-[20px] tracking-tight lg:text-[30px] 2xl:text-[35px]">{total} ₽</p>

              <div className="flex justify-end">
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>Сформировать заказ</Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px] lg:max-w-[600px]" aria-describedby="Сводка заказа">
                    <DialogHeader>
                      <DialogTitle>Сводка заказа</DialogTitle>
                    </DialogHeader>
                    <div>
                      <p className="w-full whitespace-pre-wrap rounded-md border bg-muted p-2">{cartSummary}</p>
                      <Button onClick={() => copyToClipboard(cartSummary)} className="mt-2">
                        Копировать
                      </Button>
                      <p className="mt-4 text-sm text-gray-500">
                        Это сводка вашего заказа. Вы можете скопировать ее и отправить нам для оформления заказа.
                      </p>
                    </div>
                    <Button asChild variant="secondary">
                      <Link href="https://wa.me/qr/XTLGKGVWLEMBO1">
                        <WhatsAppIcon className="mr-2 h-4 w-4" />
                        WhatsApp
                      </Link>
                    </Button>

                    <Button asChild variant="secondary">
                      <Link href="https://t.me/azimut_fix">
                        <TelegramIcon className="mr-2 h-4 w-4" />
                        Telegram
                      </Link>
                    </Button>

                    <Button variant="secondary" asChild>
                      <Link href="mailto:ems5555@list.ru">
                        <MailIcon className="mr-2 h-4 w-4" />
                        ems5555@list.ru
                      </Link>
                    </Button>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </>
        )}
      </div>
    </Wrapper>
  );
};
